import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  Button,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import React from "react";
import { useSearchParams } from "react-router-dom";
import { useFormImportTemplateDownload } from "src/components/FormImport/useFormImportTemplateDownload";
import { WithRequiredHasuraRoles } from "src/components/Permissions/WithRequiredHasuraRoles";
import type { ApplicantSummary } from "src/generated/hooks/form-service/requests";
import { HasuraRole } from "src/types/hasuraRole";
import { CreateFormForTemplateDialog } from "./CreateFormForTemplateDialog";
import { CreateFormsDialog } from "./CreateFormsDialog";

interface CreateFormsButtonProps {
  enrollmentPeriodId: uuid;
  formTemplateId: uuid;
  formTemplateName: string;
}

export function CreateFormsButton({
  enrollmentPeriodId,
  formTemplateId,
  formTemplateName,
}: CreateFormsButtonProps) {
  const createFormDisclosure = useDisclosure();
  const createFormsDisclosure = useDisclosure();
  const [searchParams, setSearchParams] = useSearchParams();
  const [reopenSeed, setReopenSeed] = React.useState<{
    student?: ApplicantSummary;
    searchText?: string;
  } | null>(null);

  const { startDownload } = useFormImportTemplateDownload({
    formTemplateId,
  });

  // Returning from the Add family flow: the create-student page passes the reopen
  // context in the URL query (same pattern as the forward hop). Reopen the dialog,
  // seeded with the new student (after save) or the original search text (on Back),
  // then strip those one-shot params so a refresh does not reopen it again.
  React.useEffect(() => {
    const reopenStudentId = searchParams.get("reopenStudentId");
    const reopenSearchText = searchParams.get("reopenSearchText");
    if (!reopenStudentId && reopenSearchText === null) return;
    createFormDisclosure.onOpen();
    setReopenSeed({
      student: reopenStudentId
        ? {
            id: reopenStudentId,
            first_name: searchParams.get("reopenStudentFirstName") ?? "",
            middle_name: null,
            last_name: searchParams.get("reopenStudentLastName") ?? "",
            birth_date: null,
            reference_id: null,
          }
        : undefined,
      searchText: reopenSearchText ?? undefined,
    });
    const next = new URLSearchParams(searchParams);
    next.delete("reopenStudentId");
    next.delete("reopenStudentFirstName");
    next.delete("reopenStudentLastName");
    next.delete("reopenSearchText");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <>
      <WithRequiredHasuraRoles roles={[HasuraRole.ADMIN]}>
        <Menu>
          <MenuButton as={Button} variant="solid" colorScheme="primary">
            <Flex gap={2} lineHeight={4} padding={3}>
              <Text>Create</Text>
              <Icon as={ChevronDownIcon} />
            </Flex>
          </MenuButton>
          <MenuList>
            <MenuItem onClick={createFormDisclosure.onOpen}>New form</MenuItem>
            <MenuItem onClick={createFormsDisclosure.onOpen}>
              New forms from file
            </MenuItem>
          </MenuList>
        </Menu>
      </WithRequiredHasuraRoles>

      <WithRequiredHasuraRoles
        roles={[HasuraRole.ORG_ADMIN, HasuraRole.SCHOOL_ADMIN]}
      >
        <Button
          variant="solid"
          colorScheme="primary"
          onClick={() => {
            createFormDisclosure.onOpen();
          }}
        >
          Create form
        </Button>
      </WithRequiredHasuraRoles>

      <CreateFormForTemplateDialog
        enrollmentPeriodId={enrollmentPeriodId}
        formTemplateId={formTemplateId}
        formTemplateName={formTemplateName}
        isOpen={createFormDisclosure.isOpen}
        onClose={() => {
          createFormDisclosure.onClose();
          setReopenSeed(null);
        }}
        initialSelectedStudent={reopenSeed?.student}
        initialSearchText={reopenSeed?.searchText}
      />

      <CreateFormsDialog
        enrollmentPeriodId={enrollmentPeriodId}
        formTemplateId={formTemplateId}
        formTemplateName={formTemplateName}
        isOpen={createFormsDisclosure.isOpen}
        onClose={createFormsDisclosure.onClose}
        onTemplateDownloadClick={startDownload}
      />
    </>
  );
}
